import accounting, { Settings as AccountingSettings } from 'accounting';
import localForage from 'localforage';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppContext,
  AppState,
  AppActions,
  AppViews,
  AppHelpers,
  AppContextResponseArray,
  AppContextResponseObject,
} from 'common/types';
import { INIT_CONTEXT, INIT_STATE } from 'common/assets';
import { I18nContext } from 'common/contexts';
import * as A from '../creators/actions';
import { createInventoryActions } from '../creators/actions/inventory';
import { createClosedOrdersViews, createTranslationHelper } from '../creators';
import { CurrencyPosition } from 'common/enums';
import { db, auth } from 'firebase/config';
import { collection, onSnapshot, getDocs, query, where, doc, getDoc, limit, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Order, Item, Category, Tax, ClosedOrder, CashRegisterState, CashMovement, User, CashierClose, InventoryItem, StockMovement, Recipe, Settings } from 'common/types';
import { getCurrentUserData, getCurrentUserUID } from 'firebase/utils';

export const store = localForage.createInstance({
  driver: localForage.INDEXEDDB,
  name: 'Resto POS',
});

export const appContext = createContext<AppContext>(INIT_CONTEXT);

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { dateDnsLocales, supportedLocales } = useContext(I18nContext);
  const { i18n, t } = useTranslation();

  const [state, setState] = useState<AppState>(INIT_STATE);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [currentUserData, setCurrentUserData] = useState<Record<string, any> | null>(null);
  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: any) => {
      // If user is logging out (was previously logged in and now null)
      if (currentUser && !user) {
        // console.log('User logged out, clearing application state');
        setState(INIT_STATE);
      }

      // Update current user state
      setCurrentUser(user ? user.uid : null);

    });

    return () => unsubscribe();
  }, [currentUser]);

  // Initialize state whenever the user changes
  useEffect(() => {
    if (currentUser) {
      // Hacerlo asíncrono y precalentar antes de inicializar
      const initializeWithPrewarming = async () => {
        try {
          // Obtener datos necesarios
          const userClaims = await getCurrentUserData();
          const parentId = userClaims?.parentId;

          // PRIMERO: Pre-calentar la conexión para operaciones de lectura de órdenes
          console.log('Pre-warming Firestore orders read operations...');
          const ordersRef = collection(db, 'orders');
          const ordersQuery = query(ordersRef, where('parentUserId', '==', parentId), limit(1));

          try {
            // Intentar leer una orden
            const snapshot = await getDocs(ordersQuery);
            console.log(`Firestore orders read connection pre-warmed (found ${snapshot.size} docs).`);
          } catch (error) {
            console.warn('Orders read pre-warming failed, continuing anyway:', error);
          }

          // Continuar con la inicialización normal
          await initializeAppState(currentUser);
        } catch (error) {
          console.error('Error during initialization with pre-warming:', error);
        }
      };

      // Ejecutar la inicialización con precalentamiento
      initializeWithPrewarming();
    }
  }, [currentUser]);

  // Function to initialize app state with user data
  const initializeAppState = async (uid: string) => {
    try {
      // console.log('Initializing app state for user:', uid);
      // const userRef = doc(db, 'users', uid);
      // const userSnap = await getDoc(userRef);

      // // Initial load of all collections
      // const data = userSnap.data();
      // console.log("🧑‍💼 Datos del usuario:", data);

      // Reemplaza tus consultas con el operador `in`


      const userClaims = await getCurrentUserData()
      const { parentId, role, name, token, uid } = userClaims
      // Ensure role is of the correct type
      const typedRole = role as 'admin' | 'editor' | string
      const keyByRole = {
        admin: where('parentUserId', '==', parentId),
        editor: where('ownerId', '==', uid)
      }

      // Actualizar el estado con el valor correcto
      setState(prevState => ({
        ...prevState,
        isAdmin: typedRole === 'admin',
        isEditor: typedRole === 'editor',
        parentId,
        name,
        // otros campos...
      }));
      // Ensure role is a valid key for keyByRole
      const Where = keyByRole[role]
      
      const ordersQuery = query(
        collection(db, 'orders'),
        Where
      );

      const closedOrdersQuery = query(
        collection(db, 'closedOrders'),
        Where
      );

      const itemsQuery = query(
        collection(db, 'items'),
        Where
      );

      const categoriesQuery = query(
        collection(db, 'categories'),
        Where
      );

      const taxesQuery = query(
        collection(db, 'taxes'),
        Where
      );

      const cashRegistersQuery = query(
        collection(db, 'cashRegister'),
        Where,
        orderBy('openDate', 'desc'),
        limit(20)
      );

      const cashMovementsQuery = query(
        collection(db, 'cashMovements'),
        Where
      );
      const usersQuery = query(
        collection(db, 'users'),
        Where
      );
      const cashierClosesQuery = query(
        collection(db, 'cashierCloses'),
          Where,
        orderBy('date', 'desc')
      );
      const settingsQuery = query(
        collection(db, 'settings'),
        Where
      );
      // Ejecuta las consultas
      const [ordersSnapshot, closedOrdersSnapshot, itemsSnapshot, categoriesSnapshot, taxesSnapshot, cashRegistersSnapshot, cashMovementsSnapshot, usersSnapshot, cashierClosesSnapshot, settingsSnapshot] = await Promise.all([
        getDocs(ordersQuery),
        getDocs(closedOrdersQuery),
        getDocs(itemsQuery),
        getDocs(categoriesQuery),
        getDocs(taxesQuery),
        getDocs(cashRegistersQuery),
        getDocs(cashMovementsQuery),
        getDocs(usersQuery),
        getDocs(cashierClosesQuery),
        getDocs(settingsQuery)
      ]);
      const orders: Order[] = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
      const closedOrders: ClosedOrder[] = closedOrdersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ClosedOrder));
      const items: Item[] = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Item));
      const categories: Category[] = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category));
      const taxes: Tax[] = taxesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Tax));
      const cashRegisters: CashRegisterState[] = cashRegistersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashRegisterState));
      const cashMovements: CashMovement[] = cashMovementsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashMovement));
      const users: User[] = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
      const cashierCloses: CashierClose[] = cashierClosesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashierClose));
      // Get settings from settingsSnapshot if it exists
      let settingsFromFirestore: Settings | undefined = undefined;
      if (settingsSnapshot && settingsSnapshot.docs.length > 0) {
        const settingsData = settingsSnapshot.docs[0].data();
        // Only use settings data if it has the required properties of Settings type

        settingsFromFirestore = settingsData as Settings;

      }
      console.log('Settings from Firestore:', settingsFromFirestore);
      // Update state with initial data
      setState(prevState => ({
        ...prevState,
        items,
        categories,
        taxes,
        orders,
        closedOrders,
        cashRegisters,
        cashMovements,
        isLoading: false,
        users,
        cashierCloses,
        ...(settingsFromFirestore ? { settings: settingsFromFirestore } : {})
      }));

      // Set up real-time listeners
      const unsubscribeOrders = onSnapshot(query(collection(db, 'orders'), Where), (snapshot) => {
        const orders: Order[] = [];
        snapshot.forEach((doc) => {
          orders.push({ id: doc.id, ...doc.data() } as Order);
        });
        setState((prevState) => {
          const currentOrder = prevState.orders.find(order => order.id == prevState.currentOrderId)
          const _new = [...orders, ...(currentOrder ? [currentOrder] : [])]
          return { ...prevState, orders: _new }
        });
      });

      const unsubscribeClosedOrders = onSnapshot(query(collection(db, 'closedOrders'), Where), (snapshot) => {
        const closedOrders: ClosedOrder[] = [];
        snapshot.forEach((doc) => {
          closedOrders.push({ id: doc.id, ...doc.data() } as ClosedOrder);
        });
        setState((prevState) => ({ ...prevState, closedOrders }));
      });

      const unsubscribeItems = onSnapshot(query(collection(db, 'items'), Where), (snapshot) => {
        const items: Item[] = [];
        snapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() } as Item);
        });
        setState((prevState) => ({ ...prevState, items }));
      });

      const unsubscribeCategories = onSnapshot(query(collection(db, 'categories'), Where), (snapshot) => {
        const categories: Category[] = [];
        snapshot.forEach((doc) => {
          categories.push({ id: doc.id, ...doc.data() } as Category);
        });
        setState((prevState) => ({ ...prevState, categories }));
      });

      const unsubscribeTaxes = onSnapshot(query(collection(db, 'taxes'), Where), (snapshot) => {
        const taxes: Tax[] = [];
        snapshot.forEach((doc) => {
          taxes.push({ id: doc.id, ...doc.data() } as Tax);
        });
        setState((prevState) => ({ ...prevState, taxes }));
      });

      const unsubscribeCashRegisters = onSnapshot(query(collection(db, 'cashRegister'), Where, orderBy('openDate', 'desc'), limit(20)), (snapshot) => {
        const cashRegisters: CashRegisterState[] = [];
        snapshot.forEach((doc) => {
          cashRegisters.push({ id: doc.id, ...doc.data() } as CashRegisterState);
        });
        setState((prevState) => ({ ...prevState, cashRegisters }));
      });

      const unsubscribeCashMovements = onSnapshot(query(collection(db, 'cashMovements'), Where), (snapshot) => {
        const cashMovements: CashMovement[] = [];
        snapshot.forEach((doc) => {
          cashMovements.push({ id: doc.id, ...doc.data() } as CashMovement);
        });
        setState((prevState) => ({ ...prevState, cashMovements }));
      });

      const unsubscribeUsers = onSnapshot(query(collection(db, 'users'), Where), (snapshot) => {
        const users: User[] = [];
        snapshot.forEach((doc) => {
          users.push({ id: doc.id, ...doc.data() } as User);
        });
        setState((prevState) => ({ ...prevState, users }));
      });

      const unsubscribeCashierCloses = onSnapshot(query(collection(db, 'cashierCloses'), Where, orderBy('date', 'desc')), (snapshot) => {
        const cashierCloses: CashierClose[] = [];
        snapshot.forEach((doc) => {
          cashierCloses.push({ id: doc.id, ...doc.data() } as CashierClose);
        });
        setState((prevState) => ({ ...prevState, cashierCloses }));
      });

      // Inventory module listeners
      console.log('Configurando listener para inventoryItems con parentId:', parentId);
      const unsubscribeInventoryItems = onSnapshot(query(collection(db, 'inventoryItems'), Where), (snapshot) => {
        console.log('Cambio detectado en inventoryItems, documentos:', snapshot.size);
        const inventoryItems: InventoryItem[] = [];
        snapshot.forEach((doc) => {
          const item = { id: doc.id, ...doc.data() } as InventoryItem;
          console.log('Item de inventario recibido:', item.id, item.name, item.quantity);
          inventoryItems.push(item);
        });
        console.log('Actualizando estado con', inventoryItems.length, 'items de inventario');
        setState((prevState) => ({ ...prevState, inventoryItems }));
      });

      console.log('Configurando listener para stockMovements con parentId:', parentId);
      const unsubscribeStockMovements = onSnapshot(query(collection(db, 'stockMovements'), Where, orderBy('date', 'desc')), (snapshot) => {
        console.log('Cambio detectado en stockMovements, documentos:', snapshot.size);
        const stockMovements: StockMovement[] = [];
        snapshot.forEach((doc) => {
          const movement = { id: doc.id, ...doc.data() } as StockMovement;
          console.log('Movimiento de stock recibido:', movement.id, movement.type, movement.quantity);
          stockMovements.push(movement);
        });
        console.log('Actualizando estado con', stockMovements.length, 'movimientos de stock');
        setState((prevState) => ({ ...prevState, stockMovements }));
      });

      const unsubscribeRecipes = onSnapshot(query(collection(db, 'recipes'), Where), (snapshot) => {
        const recipes: Recipe[] = [];
        snapshot.forEach((doc) => {
          recipes.push({ id: doc.id, ...doc.data() } as Recipe);
        });
        setState((prevState) => ({ ...prevState, recipes }));
      });
      const settings = await store.getItem('settings');
      if (settings) {
        const settingsJson = JSON.parse(settings as string);
        const { lang } = settingsJson;
        i18n.changeLanguage(lang);
        setState((prevState) => ({ ...prevState, settings: { ...settingsJson, ...settingsFromFirestore } }));
      }
      // Cleanup subscriptions on unmount
      return () => {
        unsubscribeOrders();
        unsubscribeClosedOrders();
        unsubscribeItems();
        unsubscribeCategories();
        unsubscribeTaxes();
        unsubscribeCashRegisters();
        unsubscribeCashMovements();
        unsubscribeUsers();
        unsubscribeCashierCloses();
        unsubscribeInventoryItems();
        unsubscribeStockMovements();
        unsubscribeRecipes();
      };
    } catch (error) {
      console.error('Error initializing app state:', error);
      setState(prevState => ({ ...prevState, isLoading: false }));
    }
  };


  const updateContext = (value: Partial<AppState> | ((prev: AppState) => Partial<AppState>), submit: boolean = true) => {
    try {
      if (typeof value === 'function') {
        setState(prevState => ({ ...prevState, ...value(prevState) }));
      } else {
        setState(prevState => ({ ...prevState, ...value }));
      }
    } catch (error) {
      throw new Error('Error writing data to offline storage.');
    }
  };
  const updateSettings = (value: Partial<AppState>, submit: boolean = true) => {
    store.setItem('settings', JSON.stringify(value.settings));
    setState(prevState => ({ ...prevState, settings: { ...prevState.settings, ...value.settings } }));
  };
  const helpers: AppHelpers = {
    translation: createTranslationHelper(state, { i18n, t, dateDnsLocales, supportedLocales }),
  };

  const actions: AppActions = {
    category: A.createCategoryActions(state, updateContext),
    orders: A.createOrdersActions(state, updateContext, helpers),
    item: A.createItemActions(state, updateContext),
    settings: A.createSettingsActions(state, updateContext),
    taxes: A.createTaxActions(state, updateContext),
    cashMovements: A.createCashMovementsActions(state, updateContext),
    cashier: A.createCashierActions(state, updateContext),
    inventory: createInventoryActions(state, updateContext),
  };

  const views: AppViews = {
    closedOrders: createClosedOrdersViews(state),
  };


  const responseArray: AppContextResponseArray = [state, actions, views, helpers];
  const responseObject: AppContextResponseObject = { state, actions, views, helpers };

  return <appContext.Provider value={Object.assign(responseArray, responseObject)}>{children}</appContext.Provider>;
};
